import React from 'react';
import { hot } from 'react-hot-loader/root';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import BettingReadyIndicatorProvider from '@gin-betting/components/ready-indicator';
import core from '@gin-betting/core';
import { registerLocale } from 'i18n-iso-countries';
import PropTypes from 'prop-types';
import { Store } from 'redux';

import { DEFAULT_BREAKPOINTS } from 'app-constants';
import { BettingReadyIndicatorContext } from 'modules/betting-ready-indicator';
import configureStore from 'services/configureStore';
import localeManager from 'services/locale-manager';
import UserDevice from 'services/user-device';
import { BreakpointSettings, State } from 'types';
import RootResponse from '../RootResponse';
import Platform from './Platform';

const {
    viewport: { makeViewportReducer },
    services: { reducerRegistry },
    utils: { getBettingOptions },
} = core;

// TODO: remove deprecated context API after "extendHoc" stop relying on it
const storeShape = PropTypes.shape({
    subscribe: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    getState: PropTypes.func.isRequired,
});

interface Props {
    breakpoints?: BreakpointSettings;
}

type ChildrenAsFn = (...args: any[]) => React.ReactNode;

class Root extends React.Component<Props> {
    private store: Store<State>;

    static childContextTypes = {
        store: storeShape,
    };

    constructor(props: Props) {
        super(props);

        const breakpoints = {
            ...DEFAULT_BREAKPOINTS,
            ...props.breakpoints,
        };

        const mockDevice = UserDevice.getInstance()?.isDeviceMobile()
            ? UserDevice.mockMobileDevice
            : UserDevice.mockDesktopDevice;

        reducerRegistry.register(
            'viewport',
            makeViewportReducer(
                breakpoints,
                document.body.offsetWidth || parseFloat(mockDevice.width),
                mockDevice
            )
        );

        const { i18nCountries } = localeManager.extensions;

        if (i18nCountries) {
            registerLocale(i18nCountries);
        }

        this.store = configureStore();
    }

    getChildContext() {
        return { store: this.store };
    }

    render() {
        const { locale } = getBettingOptions();
        const reactIntlLocale = localeManager.convert(locale, {
            from: 'cms',
            to: 'reactIntl',
        });
        const { messages } = localeManager.extensions;

        const { breakpoints } = this.props;
        const config = {
            store: this.store,
        };

        const children =
            typeof this.props.children === 'function'
                ? (this.props.children as ChildrenAsFn)(config)
                : this.props.children;

        return (
            <Provider store={this.store}>
                <Platform />
                <IntlProvider
                    locale={reactIntlLocale}
                    key={reactIntlLocale}
                    messages={messages}
                    textComponent="span"
                >
                    <RootResponse customBreakpoints={breakpoints}>
                        <BettingReadyIndicatorProvider
                            BettingReadyIndicatorContext={
                                BettingReadyIndicatorContext
                            }
                        >
                            {React.Children.only(children)}
                        </BettingReadyIndicatorProvider>
                    </RootResponse>
                </IntlProvider>
            </Provider>
        );
    }
}

export default hot(Root);
